/**
 * Represents a resource from the /customers/ API collection.
 */
import type { ActivatedRoute, ParamMap } from '@angular/router';
import {
  getDaysAndMonthsString,
  loadBoolFilterFromGetParams,
  loadFloatFilterFromGetParams,
  loadIntFilterFromGetParams,
  loadStringFilterFromGetParams
} from '../reporting-core/utils';
import { BookingStatus } from './booking-enums';
import { FilterMatchMode } from './filter-match-mode';
import { CustomerMembershipStatus } from './membership-enums';
import { LineItemPurchaseType, PaymentMethodType, PaymentStatus, PaymentType } from './payment-enums';

export enum Gender {
  male = 'M',
  female = 'F',
  nonbinary = 'N',
  prefer_not_to_say = 'P',
  not_given = ''
}

export enum RelationType {
  spouse = 'SPO',
  partner = 'PRT',
  parent = 'PRE',
  child = 'CHI',
  legal_guardian = 'LGU',
  legal_ward = 'LWA',
  guardian = 'GUA',
  ward = 'WAR',
  sibling = 'SIB',
  other = 'OTH'
}

export enum CheckInEntryMethod {
  membership = 'MEM',
  entryPass = 'ENT',
  eventRegistration = 'EVE',
  guestPass = 'GUE',
  freeEntry = 'FRE',
  migrated = 'MIG'
}

export enum CustomerReservationPaymentStatus {
  paymentPending = 'PEN',
  depositPaid = 'DEP',
  paymentComplete = 'COM',
  partiallyRefunded = 'PRF',
  refunded = 'REF',
  cancelled = 'CAN',
}

export enum PendingRelationStatus {
  pendingRelatedCustomerApproval = 'P1',
  pendingStaffApproval = 'P2',
  approved = 'AP',
  cancelled = 'CA',
  rejectedByRelatedCustomer = 'R1',
  rejectedByStaff = 'R2'
}

export enum PendingCustomerRelationAction {
  approve = 'approve',
  deny = 'deny',
  hide = 'hide'
}

export enum GuestPassStatus {
  notYetAvailable = 'NYA',
  available = 'AVA',
  assigned = 'ASI',
  used = 'USE',
  expired = 'EXP',
  removed = 'REM'
}

/**
 * Who may redeem a guest pass (PRO-1419). Mirrors CustomerGuestPass.Eligibility.
 */
export enum GuestPassEligibility {
  // Least to most restrictive, which is also the order the eligibility
  // dropdowns use.
  allCustomers = 'ALL',
  lapsedCustomersOnly = 'LAP',
  newCustomersOnly = 'NEW'
}

export enum CustomerPaymentMethodType {
  card = 'CRD',
  bank_account = 'BNK'
}

export enum CustomerActivityType {
  accountCreated = 'CRE',
  checkedInToFacility = 'CKF',
  checkedInToEvent = 'CKE',
  documentCompleted = 'DOC',
  purchaseInitiated = 'PRI',
  purchaseCompleted = 'PRC',
  joinedEventWaitlist = 'WAI',
  addedAsEventParticipant = 'EVA',
  profileEdited = 'PRE',
}

/**
 * The derived state of a customer's memberships (PRO-1503), set by the
 * customer-profile-details endpoint: the first state any membership matches,
 * so active wins over frozen.
 */
export enum MembershipStatusSummary {
  active = 'ACT',
  pendingStaffReview = 'PSR',
  frozen = 'FRZ',
  paymentFailed = 'PAF',
  paymentPending = 'PPE',
  awaitingPaymentMethod = 'APM',
  futureStart = 'FUT',
  none = 'NON',
}

export interface CustomCustomerFieldValue {
  field_id: number;
  field_name: string;
  value: string;
}

export interface Customer {
  id: number;
  url: string;
  email: string;
  first_name: string;
  last_name: string;
  preferred_name: string;
  birthday: string;
  address_postcode: string;
  address_street_1: string;
  address_street_2: string;
  address_city: string;
  address_state: string;
  address_country: string;
  government_id: string;
  telephone: string;
  gender: Gender;
  pronouns: string;
  has_opted_in_to_marketing: boolean;
  barcode: string;
  profile_image?: string;
  profile_image_small?: string;
  home_location_id?: number;
  home_location_url?: string;
  home_location_name?: string;
  capitan_invite_sent: boolean;
  has_capitan_login: boolean;
  check_in_count: number;
  has_active_membership: boolean;
  has_active_youth_programming_membership: boolean;
  has_active_non_youth_programming_membership: boolean;
  has_membership_with_future_start: boolean;
  has_frozen_membership: boolean;
  number_of_available_entry_passes: number;
  active_waiver_exists: boolean;
  latest_waiver_expiration_date?: string;
  active_proficiency_names: string[];
  tags: string[];
  custom_field_values: CustomCustomerFieldValue[];
  has_medical_notes: boolean;
  has_allthenticate_account: boolean;
  allthenticate_invite_sent_at: string | null;
  first_check_in_timestamp: string;
  latest_check_in_timestamp: string;
  days_since_latest_check_in: number | null;
  first_membership_timestamp: string | null;
  last_activity_timestamp: string;
  last_activity_type: CustomerActivityType;
  last_activity_location_id: number | null;
  last_activity_location_name: string | null;
  created_at: string;
  updated_at: string;
  event_vouchers_url: string;
  credits_url: string;
  emergency_contacts_url: string;
  medical_notes_url: string;
  notes_url: string;
  alerts_url: string;
  documents_url: string;
  relations_url: string;
  check_ins_url: string;
  memberships_url: string;
  entry_passes_url: string;
  payments_url: string;
  proficiencies_url: string;
  pending_relations_url: string;
  incoming_pending_relations_url: string;
  activity_log_url: string;
  guest_passes_url: string;

  // Only available via getCustomersWithCheckInCountInRange
  check_in_range_count?: number;
}

export interface CustomerSearchResult {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  preferred_name: string;
  birthday: string;

  // Only available via getCustomersWithCheckInCountInRange
  check_in_range_count?: number;
}

export interface CustomerRecentActivityResult {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  preferred_name: string;
  birthday: string;
  last_activity_timestamp: string;
  last_activity_type: CustomerActivityType;
  last_activity_location_id: number | null;
  last_activity_location_name: string | null;
}

export interface CustomerActiveProficiency {
  id: number;
  proficiency_id: number;
  proficiency_name: string;
  proficiency_icon_color_hex: string;
  proficiency_icon_initials: string;
  expiration_date?: string;
}

export interface CustomerMembershipIcon {
  icon_color_hex: string;
  icon_initials: string;
  icon_hover_text: string;
}

export interface CustomerProfileDetails {
  has_membership_recurring: boolean;
  has_membership_prepaid: boolean;
  membership_end_date: string;
  membership_status: MembershipStatusSummary;
  // The date belonging to membership_status: when active, the scheduled stop
  // (null for a plain recurring membership — its next bill date is not an
  // end); when frozen, the unfreeze date; when not started, the start date.
  membership_status_date: string | null;
  check_in_alerts: string[];
  active_proficiencies: CustomerActiveProficiency[];
  membership_icons: CustomerMembershipIcon[];
  remaining_guest_passes: number;
  remaining_guest_passes_all_customers: number;
  last_check_in_date: string | null;
  mailchimp_group_ids: number[];
  has_alert_icon: boolean;
  has_emergency_contact: boolean;
  most_recent_medical_notes: string | null;
  referral_code: string;
}

/** The customer-latest-check-in endpoint's response, polled by Customer Detail (PRO-1449). */
export interface CustomerLatestCheckIn {
  latest_check_in_timestamp: string | null;
}

export interface RootCustomerEventVoucher {
  id: number;
  url: string;
  customer_id: number | null;
  customer_first_name: string | null;
  customer_last_name: string | null;
  customer_email: string | null;
  event_type_id: number;
  event_type_url: string;
  event_type_name: string;
  event_category_id: number;
  event_category_url: string;
  event_category_name: string;
  number_of_uses_total: number;
  number_of_uses_remaining: number;
  expiry_date: string | null;
  expiry_date_str: string | null;
  is_expired: boolean;
  is_removed: boolean;
  voucher_pack_id: number | null;
  voucher_pack_name: string | null;
  membership_id: number | null;
  membership_name: string | null;
  created_by_id: number;
  created_by_first_name: string;
  created_by_last_name: string;
  created_by_email: string;
  purchaser_id: number | null;
  purchaser_first_name: string | null;
  purchaser_last_name: string | null;
  purchaser_email: string | null;
  invoice_id: number | null;
  invoice_location_id: number | null;
  invoice_location_name: string | null;
  line_item_id: number | null;
  line_item_amount: number | null;
  date_added: string;
  date_added_str: string;
  created_at: string;
  updated_at: string;
}

export interface CustomerEventVoucher {
  id: number;
  url: string;
  event_type_id: number;
  event_type_url: string;
  event_type_name: string;
  event_category_id: number;
  event_category_url: string;
  event_category_name: string;
  number_of_uses_total: number;
  number_of_uses_remaining: number;
  expiry_date?: string | null;
  expiry_date_str?: string | null;
  is_expired: boolean;
  is_removed: boolean;
  voucher_pack_id?: number | null;
  voucher_pack_name?: string | null;
  membership_id?: number | null;
  membership_name?: string | null;
  created_by_id?: number;
  created_by_first_name?: string;
  created_by_last_name?: string;
  created_by_email?: string;
  purchaser_id?: number | null;
  purchaser_first_name?: string | null;
  purchaser_last_name?: string | null;
  purchaser_email?: string | null;
  invoice_id?: number | null;
  date_added: string;
  date_added_str: string;
  created_at: string;
  updated_at: string;
}

export interface CustomerCredit {
  id: number;
  url: string;
  amount_total: number;
  amount_remaining: number;
  expiry_date?: string;
  expiry_date_str?: string;
  is_expired: boolean;
  is_removed: boolean;
  will_apply_to_recurring_payments: boolean;
  only_applies_to_recurring_payments: boolean;
  created_by_id?: number;
  created_by_first_name?: string;
  created_by_last_name?: string;
  created_by_email?: string;
  date_added_str: string;
  created_at: string;
  updated_at: string;
}

export interface CustomerEmergencyContact {
  id: number;
  url: string;
  first_name: string;
  last_name: string;
  telephone: string;
  email: string;
  relation: string;
  is_removed: boolean;
  created_at: string;
  updated_at: string;
}

export interface CustomerMedicalNotes {
  id: number;
  url: string;
  medical_notes: string;
  created_at: string;
  updated_at: string;
}

export interface CustomerNote {
  id: number;
  url: string;
  title: string;
  note: string;
  is_removed: boolean;
  created_by_id: number | null;
  created_by_email: string | null;
  updated_by_id: number | null;
  updated_by_email: string | null;
  created_date: string;
  updated_date: string;
  created_at: string;
  updated_at: string;
}

export interface CustomerAlert {
  id: number;
  url: string;
  alert_text: string;
  is_removed: boolean;
  expiry_date: string;
  color_hex: string;
  prevents_check_in: boolean;
  has_alert_icon: boolean;
  created_by_id: number | null;
  created_by_email: string | null;
  updated_by_id: number | null;
  updated_by_email: string | null;
  created_date: string;
  updated_date: string;
  created_at: string;
  updated_at: string;
}

export interface CustomerDocument {
  id: string;
  url: string;
  name: string;
  pdf: string;
  expiration_date?: string;
  is_expired: boolean;
  is_invalid: boolean;
  is_pending_staff_approval: boolean;
  is_denied_staff_approval: boolean;
  created_at: string;
  updated_at: string;
}

export interface CustomerRelation {
  id: number;
  url: string;
  related_customer_id: number;
  related_customer_url: string;
  related_customer_email: string;
  related_customer_first_name: string;
  related_customer_last_name: string;
  related_customer_birthday: string | null;
  related_customer_profile_image_url: string;
  related_customer_profile_image_small_url: string;
  relation: RelationType;
  created_at: string;
  updated_at: string;
}

/**
 * The relations the invitation email helpers add for
 * send_invite_to_parents_and_guardians; must match `apps/event/helpers_email.py`.
 */
export const GUARDIAN_RELATION_TYPES = [
  RelationType.parent,
  RelationType.legal_guardian,
  RelationType.guardian,
];

/** Whether this relation is a guardian the invitation helpers would actually email. */
export function isGuardianWithEmail(relation: CustomerRelation): boolean {
  return (
    (GUARDIAN_RELATION_TYPES.indexOf(relation.relation) !== -1) &&
    !!relation.related_customer_email
  );
}

export interface CustomerCheckIn {
  id: number;
  url: string;
  check_in_datetime: string;
  check_out_datetime?: string;
  visit_duration_minutes: number;
  entry_method: CheckInEntryMethod;
  entry_method_description: string;
  location_id: number;
  location_url: string;
  location_name: string;
  free_entry_reason: string;
  free_entry_granting_user_id?: number;
  free_entry_granting_user_first_name?: string;
  free_entry_granting_user_last_name?: string;
  converted_from_free_entry: boolean;
  milestone_list: string[];
  check_in_count: number;
  created_at: string;
  updated_at: string;
}

export interface CustomerGuestPassUsage {
  id: number;
  url: string;
  checked_in_customer_id: number;
  checked_in_customer_url: string;
  checked_in_customer_first_name: string;
  checked_in_customer_last_name: string;
  check_in_datetime: string;
  entry_method: CheckInEntryMethod;
  guest_was_new_customer: boolean;
  created_at: string;
  updated_at: string;
}

export interface CustomerSharedPass {
  id: number;
  url: string;
  shared_customer_id: number;
  shared_customer_url: string;
  shared_customer_first_name: string;
  shared_customer_last_name: string;
  check_in_datetime: string;
  entry_method: CheckInEntryMethod;
  entry_method_description: string;
  created_at: string;
  updated_at: string;
}

export interface CustomerPaymentLineItem {
  id: number;
  purchase_type: LineItemPurchaseType;
  description: string;
  amount: number;
}

export interface CustomerPayment {
  id: number;
  url: string;
  status: PaymentStatus;
  amount: number;
  amount_refunded: number;
  pending_refund_amount?: number | null;
  payment_type: PaymentType;
  payment_method_type: PaymentMethodType;
  credit_card_last4: string;
  credit_card_brand: string;
  bank_account_last4: string;
  bank_account_bank_name: string;
  created_date: string;
  invoice_id: number;
  invoice_description: string;
  line_items: CustomerPaymentLineItem[];
  discount_amount: number;
  discount_code: string;
  discount_name: string;
  discount_display_name: string;
  created_at: string;
  updated_at: string;
}

export interface CustomerProficiency {
  id: number;
  url: string;
  proficiency_id: number;
  proficiency_url: string;
  proficiency_name: string;
  proficiency_icon_color_hex: string;
  proficiency_icon_initials: string;
  proficiency_is_removed: boolean;
  expiration_date?: string;
  is_expired: boolean;
  is_inactive_due_to_doc: boolean;
  granting_document_id?: number;
  granting_document_name?: string;
  granting_document_is_pending_staff_approval?: boolean;
  granting_document_is_denied_staff_approval?: boolean;
  granting_document_is_invalid?: boolean;
  created_by_id?: number;
  created_by_first_name?: string;
  created_by_last_name?: string;
  created_by_email?: string;
  last_edited_by_id?: number;
  last_edited_by_email?: string;
  created_at: string;
  updated_at: string;
}

export interface CustomerReservation {
  id: number;
  url: string;
  is_cancelled: boolean;
  payment_status: CustomerReservationPaymentStatus;
  invoice_id: number;
  fee_name: string;
  add_on_names: string[];
  add_on_names_display?: string[];
  event_type_name: string;
  created_at: string;
  updated_at: string;
}

export interface CustomerReservationPart {
  id: number;
  // The whole reservation this session belongs to; what the reservation
  // details modal is addressed by (PRO-1514).
  reservation_id: number;
  url: string;
  is_cancelled: boolean;
  payment_status: CustomerReservationPaymentStatus;
  invoice_id: number;
  fee_name: string;
  add_on_names: string[];
  add_on_names_display?: string[];
  start_local_date: string;
  start_local_time: string;
  start_datetime: string;
  end_local_date: string;
  end_local_time: string;
  end_datetime: string;
  event_type_name: string;
  event_part_id: number;
  created_at: string;
  updated_at: string;
}

/** Which of a customer's reservations to list (PRO-1338). */
export enum CustomerReservationTimeScope {
  upcoming = 'upcoming',
  all = 'all',
}

export interface CustomerReservationPartFilters {
  include_nonpayment_cancelled?: boolean;
  time_scope?: CustomerReservationTimeScope;
}

/** A participant on a booking; `customer_id` is null when never linked to a customer record. */
export interface CustomerBookingParticipant {
  customer_id: number | null;
  first_name: string;
  last_name: string;
  is_cancelled: boolean;
}

/** The customer who made a booking; null on the booking when made without logging in. */
export interface CustomerBookingBookedBy {
  customer_id: number;
  first_name: string;
  last_name: string;
}

/** A `/customers/<id>/bookings/` item: a booking the customer made or is in (PRO-1338). */
export interface CustomerBooking {
  id: number;
  url: string;
  event_id: number;
  event_type_name: string;
  event_first_part_id: number | null;
  event_start_datetime: string | null;
  event_end_datetime: string | null;
  event_start_local_date: string | null;
  created_local_date: string;
  status: BookingStatus;
  is_cancelled: boolean;
  invoice_id: number;
  payment_status: CustomerReservationPaymentStatus;
  invoice_amount_due: number;
  // Distinguishes a free booking from a payable one (every booking has an
  // invoice); keyed on the price BEFORE discounts, so a comped booking counts.
  has_payable_invoice: boolean;
  num_participants: number;
  // Capped server-side; `num_participants` is the real total.
  participants: CustomerBookingParticipant[];
  booked_by: CustomerBookingBookedBy | null;
  is_booked_by_this_customer: boolean;
  created_at: string;
  updated_at: string;
}

export interface CustomerBookingFilters {
  include_roster_bookings?: boolean;
  include_nonpayment_cancelled?: boolean;
}


export interface CustomerTag {
  id: number;
  url: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface PendingCustomerRelation {
  id: number;
  url: string;
  related_customer_id: number;
  related_customer_url: string;
  related_customer_email: string;
  related_customer_first_name: string;
  related_customer_last_name: string;
  relation: RelationType;
  status: PendingRelationStatus;
  created_at: string;
  updated_at: string;
}

export interface IncomingPendingCustomerRelation {
  id: number;
  url: string;
  requesting_customer_id: number;
  requesting_customer_url: string;
  requesting_customer_email: string;
  requesting_customer_first_name: string;
  requesting_customer_last_name: string;
  relation: RelationType;
  status: PendingRelationStatus;
  created_at: string;
  updated_at: string;
}

// The version of PendingCustomerRelation returned from the "Linking Account
// Requests" view.
export interface PendingCustomerRelationRequestView {
  id: number;
  requesting_customer_id: number;
  requesting_customer_email: string;
  requesting_customer_first_name: string;
  requesting_customer_last_name: string;
  related_customer_id: number;
  related_customer_email: string;
  related_customer_first_name: string;
  related_customer_last_name: string;
  relation: RelationType;
  created_at: string;
}

export interface UnmatchedLinkRequestLog {
  id: number;
  requesting_customer_id: number;
  requesting_customer_email: string;
  requesting_customer_first_name: string;
  requesting_customer_last_name: string;
  first_name_input: string;
  last_name_input: string;
  birthday_input: string;
  relation_input: RelationType;
  created_at: string;
}

export interface MemberAncillaryRevenueByCustomerItem {
  id: number;
  first_name: string;
  last_name: string;
  birthday: string;
  email: string | null;
  telephone: string;
  membership_id: number;
  membership_name: string;
  ancillary_revenue_amount: number;
}

export interface GuestPass {
  id: number;
  url: string;
  owner_id: number;
  owner_first_name: string;
  owner_last_name: string;
  owner_birthday: string;
  owner_email: string | null;
  assignee_id: number | null;
  assignee_first_name: string | null;
  assignee_last_name: string | null;
  assignee_birthday: string | null;
  assignee_email: string | null;
  status: GuestPassStatus;
  eligibility: GuestPassEligibility;
  // Why the customer named by the `check_in_customer_id` filter may not
  // redeem this pass; null when they may, or when no customer was named.
  ineligibility_reason: string | null;
  granting_membership_id: number | null;
  granting_membership_status: CustomerMembershipStatus | null;
  granting_membership_type_id: number | null;
  granting_membership_type_name: string | null;
  granting_user_id: number | null;
  granting_user_first_name: string | null;
  granting_user_last_name: string | null;
  granting_user_email: string | null;
  name: string;
  available_starting_at: string | null;
  available_starting_at_date: string | null;
  expires_at: string | null;
  expires_at_date: string | null;
  used_at: string | null;
  used_at_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface CustomerPaymentMethod {
  id: number;
  payment_processor_connection_id: number;
  payment_method_type: CustomerPaymentMethodType;
  exp_month: number;
  exp_year: number;
  last4: string;
  brand: string;
  account_type: string;
  bank_name: string;
  account_holder_name: string;
  is_wallet_token: boolean;
  wallet_type: string;
  created_at: string;
  updated_at: string;
}

/**
 * Represents the set of field filters available for the /customers/ API
 * collection.
 */
export interface CustomerFilters {
  // General-purpose text search.
  search?: string;
  age_min?: number;
  age_max?: number;
  birthday_min?: string;
  birthday_max?: string;
  created_at_min?: string;
  created_at_max?: string;
  created_at_range?: string;
  address_postcode?: string;
  gender?: Gender;
  has_opted_in_to_marketing?: boolean;
  check_in_count_min?: number;
  check_in_count_max?: number;
  first_membership_timestamp_min?: string;
  first_membership_timestamp_max?: string;
  first_membership_timestamp_range?: string;
  has_active_membership?: boolean;
  has_active_youth_programming_membership?: boolean;
  has_active_non_youth_programming_membership?: boolean;
  has_membership_with_future_start?: boolean;
  member_active_membership_name?: string;
  has_frozen_membership?: boolean;
  number_of_available_entry_passes_min?: number;
  number_of_available_entry_passes_max?: number;
  tags?: string;
  tags_match?: FilterMatchMode;
  has_event_type_reservation?: string;
  latest_check_in_timestamp_min?: string;
  latest_check_in_timestamp_max?: string;
  latest_check_in_timestamp_range?: string;
  latest_check_in_timestamp_days_min?: number;
  latest_check_in_timestamp_days_max?: number;
  had_check_in_during_date_range?: string;
  has_proficiencies?: string;
  has_proficiencies_match?: FilterMatchMode;
  has_association_memberships?: string;
  has_association_memberships_match?: FilterMatchMode;
  has_challenge?: string;
  has_challenge_match?: FilterMatchMode;
  last_activity_timestamp_min?: string;
  last_activity_timestamp_max?: string;
  home_location_id?: number;
  has_profile_image?: boolean;

  // Only used by getCustomersWithCheckInCountInRange
  check_in_range_start?: string;
  check_in_range_end?: string;
  check_in_range_count_min?: number;
  check_in_range_count_max?: number;
}

/** The CustomerFilters keys holding an all/any match mode for a sibling multi-value filter (PRO-1373). */
export type CustomerFilterMatchModeKey =
  'has_proficiencies_match' | 'has_association_memberships_match' |
  'tags_match' | 'has_challenge_match';

export interface RootCustomerEventVoucherFilters {
  created_at_min?: string;
  created_at_max?: string;
  created_at_range?: string;
  number_of_uses_remaining_min?: number;
  number_of_uses_remaining_max?: number;
  is_voucher_pack_purchase?: boolean;
  invoice_location_id?: number;
  is_removed?: boolean;
  is_expired?: boolean;
}

/** Filters the Event Vouchers page seeds on every load; an explicit null turns one off. */
export const ROOT_CUSTOMER_EVENT_VOUCHER_FILTER_DEFAULTS: RootCustomerEventVoucherFilters = Object.freeze({
  is_removed: false,
  is_expired: false,
  number_of_uses_remaining_min: 1,
});

export interface MemberAncillaryRevenueByCustomerFilters {
  location_id?: number;
  membership_id?: number;
  date_min?: string;
  date_max?: string;
  revenue_min?: number;
  revenue_max?: number;
}

export interface GuestPassFilters {
  status?: GuestPassStatus;
  eligibility?: GuestPassEligibility;
  owner_id?: number;
  // Fills each pass's `ineligibility_reason` for this customer (PRO-1419).
  check_in_customer_id?: number;
}

/**
 * Load the `<valueKey>_match` companion param (PRO-1373). Stored only when it
 * is exactly 'any' AND the sibling filter was loaded: 'all' is an absent key,
 * and an orphaned mode would persist invisibly (the modal's select only
 * renders alongside its sibling).
 */
function loadFilterMatchModeFromGetParams(
  filters: CustomerFilters,
  valueKey: 'has_proficiencies' | 'has_association_memberships' | 'tags' | 'has_challenge',
  queryParamMap: ParamMap,
) {
  const matchKey = (valueKey + '_match') as CustomerFilterMatchModeKey;

  if (filters[valueKey] && queryParamMap.get(matchKey) === FilterMatchMode.any) {
    filters[matchKey] = FilterMatchMode.any;
  }
}

export function loadCustomerFiltersFromGetParams(activatedRoute: ActivatedRoute): CustomerFilters {
  const filters: CustomerFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadStringFilterFromGetParams(filters, 'search', initialQPs);
  loadIntFilterFromGetParams(filters, 'age_min', initialQPs);
  loadIntFilterFromGetParams(filters, 'age_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'birthday_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'birthday_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_range', initialQPs);
  loadStringFilterFromGetParams(filters, 'address_postcode', initialQPs);
  loadStringFilterFromGetParams(filters, 'gender', initialQPs);
  loadBoolFilterFromGetParams(filters, 'has_opted_in_to_marketing', initialQPs);
  loadIntFilterFromGetParams(filters, 'check_in_count_min', initialQPs);
  loadIntFilterFromGetParams(filters, 'check_in_count_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'first_membership_timestamp_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'first_membership_timestamp_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'first_membership_timestamp_range', initialQPs);
  loadBoolFilterFromGetParams(filters, 'has_active_membership', initialQPs);
  loadBoolFilterFromGetParams(filters, 'has_active_youth_programming_membership', initialQPs);
  loadBoolFilterFromGetParams(filters, 'has_active_non_youth_programming_membership', initialQPs);
  loadBoolFilterFromGetParams(filters, 'has_membership_with_future_start', initialQPs);
  loadStringFilterFromGetParams(filters, 'member_active_membership_name', initialQPs);
  loadBoolFilterFromGetParams(filters, 'has_frozen_membership', initialQPs);
  loadIntFilterFromGetParams(filters, 'number_of_available_entry_passes_min', initialQPs);
  loadIntFilterFromGetParams(filters, 'number_of_available_entry_passes_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'tags', initialQPs);
  loadFilterMatchModeFromGetParams(filters, 'tags', initialQPs);
  loadStringFilterFromGetParams(filters, 'has_event_type_reservation', initialQPs);
  loadStringFilterFromGetParams(filters, 'latest_check_in_timestamp_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'latest_check_in_timestamp_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'latest_check_in_timestamp_range', initialQPs);
  loadIntFilterFromGetParams(filters, 'latest_check_in_timestamp_days_min', initialQPs);
  loadIntFilterFromGetParams(filters, 'latest_check_in_timestamp_days_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'had_check_in_during_date_range', initialQPs);
  loadStringFilterFromGetParams(filters, 'has_proficiencies', initialQPs);
  loadFilterMatchModeFromGetParams(filters, 'has_proficiencies', initialQPs);
  loadStringFilterFromGetParams(filters, 'has_association_memberships', initialQPs);
  loadFilterMatchModeFromGetParams(filters, 'has_association_memberships', initialQPs);
  loadStringFilterFromGetParams(filters, 'has_challenge', initialQPs);
  loadFilterMatchModeFromGetParams(filters, 'has_challenge', initialQPs);
  loadStringFilterFromGetParams(filters, 'last_activity_timestamp_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'last_activity_timestamp_max', initialQPs);
  loadIntFilterFromGetParams(filters, 'home_location_id', initialQPs);
  loadBoolFilterFromGetParams(filters, 'has_profile_image', initialQPs);

  // Only used by getCustomersWithCheckInCountInRange
  loadStringFilterFromGetParams(filters, 'check_in_range_start', initialQPs);
  loadStringFilterFromGetParams(filters, 'check_in_range_end', initialQPs);
  loadIntFilterFromGetParams(filters, 'check_in_range_count_min', initialQPs);
  loadIntFilterFromGetParams(filters, 'check_in_range_count_max', initialQPs);

  return filters;
}

export function loadRootCustomerEventVoucherFiltersFromGetParams(activatedRoute: ActivatedRoute): RootCustomerEventVoucherFilters {
  const filters: RootCustomerEventVoucherFilters = {...ROOT_CUSTOMER_EVENT_VOUCHER_FILTER_DEFAULTS};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadStringFilterFromGetParams(filters, 'created_at_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_range', initialQPs);
  loadIntFilterFromGetParams(filters, 'invoice_location_id', initialQPs);
  loadIntFilterFromGetParams(filters, 'number_of_uses_remaining_min', initialQPs);
  loadIntFilterFromGetParams(filters, 'number_of_uses_remaining_max', initialQPs);
  loadBoolFilterFromGetParams(filters, 'is_voucher_pack_purchase', initialQPs);
  loadBoolFilterFromGetParams(filters, 'is_removed', initialQPs);
  loadBoolFilterFromGetParams(filters, 'is_expired', initialQPs);

  // An empty param is this page's own spelling of "no filter"; the shared
  // loaders leave it undefined or skip it, both of which would read as the default.
  for (const key of Object.keys(ROOT_CUSTOMER_EVENT_VOUCHER_FILTER_DEFAULTS)) {
    if (initialQPs.get(key) === '') {
      filters[key] = null;
    }
  }

  return filters;
}

export function loadMemberAncillaryRevenueByCustomerFiltersFromGetParams(
    activatedRoute: ActivatedRoute): MemberAncillaryRevenueByCustomerFilters {
  const filters: MemberAncillaryRevenueByCustomerFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadIntFilterFromGetParams(filters, 'location_id', initialQPs);
  loadIntFilterFromGetParams(filters, 'membership_id', initialQPs);
  loadStringFilterFromGetParams(filters, 'date_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'date_max', initialQPs);
  loadFloatFilterFromGetParams(filters, 'revenue_min', initialQPs);
  loadFloatFilterFromGetParams(filters, 'revenue_max', initialQPs);

  return filters;
}

/**
 * Get the human-readable name of the given gender.
 *
 * @param gender - The gender.
 */
export function getGenderDisplay(gender: Gender): string {
  switch (gender) {
    case Gender.male:
      return 'Male';
    case Gender.female:
      return 'Female';
    case Gender.nonbinary:
      return 'Non-binary';
    case Gender.prefer_not_to_say:
      return 'Prefer not to say';
    case Gender.not_given:
      return 'Not given';
    default:
      return 'Unknown Gender';
  }
}

/**
 * Get the human-readable name of the given relation type.
 *
 * @param relationType - The relation type.
 */
export function getRelationTypeDisplay(relationType: RelationType): string {
  switch (relationType) {
    case RelationType.spouse:
      return 'Spouse';
    case RelationType.partner:
      return 'Partner';
    case RelationType.parent:
      return 'Parent';
    case RelationType.child:
      return 'Child';
    case RelationType.legal_guardian:
      return 'Legal Guardian';
    case RelationType.legal_ward:
      return 'Legal Ward';
    case RelationType.guardian:
      return 'Guardian';
    case RelationType.ward:
      return 'Ward';
    case RelationType.sibling:
      return 'Sibling';
    case RelationType.other:
      return 'Other';
    default:
      return 'Unknown Relation';
  }
}

export function getCheckInEntryMethodDisplay(entryMethod: CheckInEntryMethod): string {
  switch (entryMethod) {
    case CheckInEntryMethod.membership:
      return 'Membership';
    case CheckInEntryMethod.entryPass:
      return 'Entry Pass';
    case CheckInEntryMethod.eventRegistration:
      return 'Event';
    case CheckInEntryMethod.guestPass:
      return 'Guest Pass';
    case CheckInEntryMethod.freeEntry:
      return 'Free Entry';
    case CheckInEntryMethod.migrated:
      return 'Migrated Data';
    default:
      return 'Unknown Entry Method';
  }
}

export function getGuestPassStatusDisplay(status:GuestPassStatus): string {
  switch (status) {
    case GuestPassStatus.notYetAvailable:
      return 'Not Yet Available';
    case GuestPassStatus.available:
      return 'Available';
    case GuestPassStatus.assigned:
      return 'Assigned';
    case GuestPassStatus.used:
      return 'Used';
    case GuestPassStatus.expired:
      return 'Expired';
    case GuestPassStatus.removed:
      return 'Removed';
    default:
      return 'Unknown Status';
  }
}


/**
 * The "can be used by" label for a guest pass; the lapsed label carries the
 * org's `lapsed_customer_guest_pass_window_months` (null while unloaded).
 */
export function getGuestPassEligibilityDisplay(eligibility: GuestPassEligibility,
                                               lapsedWindowMonths: number | null): string {
  switch (eligibility) {
    case GuestPassEligibility.allCustomers:
      return 'All Customers';
    case GuestPassEligibility.lapsedCustomersOnly:
      if (lapsedWindowMonths === null || lapsedWindowMonths === undefined) {
        return 'Lapsed Customers Only';
      }
      return 'Lapsed Customers Only: no check-in in the last ' +
        getDaysAndMonthsString(null, lapsedWindowMonths);
    case GuestPassEligibility.newCustomersOnly:
      return 'New Customers Only';
    default:
      return 'Unknown';
  }
}

/**
 * Help text for a guest pass eligibility, '' for All Customers. The lapsed
 * case's "configured under Organization Settings" sentence lives in each
 * template instead, because it carries a routerLink.
 */
export function getGuestPassEligibilityDescription(eligibility: GuestPassEligibility,
                                                   lapsedWindowMonths: number | null): string {
  switch (eligibility) {
    case GuestPassEligibility.newCustomersOnly:
      return 'These guest passes may only be used by customers who have never checked in previously.';
    case GuestPassEligibility.lapsedCustomersOnly:
      const windowText = (
        (lapsedWindowMonths === null || lapsedWindowMonths === undefined)
          ? 'the configured number of months'
          : getDaysAndMonthsString(null, lapsedWindowMonths)
      );
      return 'These guest passes may be used by customers who have never checked in, or whose most recent ' +
        'check-in at any of your locations was more than ' + windowText + ' ago.';
    default:
      return '';
  }
}

export function getCustomerCustomFieldValue(customer: Customer, fieldName: string) {
  if (!customer.custom_field_values) { return null; }
  for (const fieldValue of customer.custom_field_values) {
    if (fieldValue.field_name === fieldName) { return fieldValue.value; }
  }
  return null;
}

export function getGuestPassSourceText(guestPass: GuestPass): string {
  if (guestPass.granting_membership_type_name) {
    return guestPass.granting_membership_type_name;
  } else if (guestPass.name) {
    return 'Manual: ' + guestPass.name;
  } else {
    return 'Manual';
  }
}

export function getLastActivityText(customer: Customer | CustomerRecentActivityResult,
                                    includeLocation: boolean): string {
  let output = '';
  switch (customer.last_activity_type) {
    case CustomerActivityType.accountCreated:
      return 'Account created';
    case CustomerActivityType.checkedInToFacility:
      output = 'Checked in to '
      if (customer.last_activity_location_name) {
        output += customer.last_activity_location_name;
      } else {
        output += 'a location';
      }
      return output;
    case CustomerActivityType.checkedInToEvent:
      output = 'Checked in to an event';
      if (includeLocation && customer.last_activity_location_name) {
        output += ' at ' + customer.last_activity_location_name;
      }
      return output;
    case CustomerActivityType.documentCompleted:
      return 'Completed a document';
    case CustomerActivityType.purchaseInitiated:
      output = 'Initiated a purchase';
      if (includeLocation && customer.last_activity_location_name) {
        output += ' at ' + customer.last_activity_location_name;
      }
      return output;
    case CustomerActivityType.purchaseCompleted:
      output = 'Completed a purchase';
      if (includeLocation && customer.last_activity_location_name) {
        output += ' at ' + customer.last_activity_location_name;
      }
      return output;
    case CustomerActivityType.joinedEventWaitlist:
      output = 'Joined an event waitlist';
      if (includeLocation && customer.last_activity_location_name) {
        output += ' at ' + customer.last_activity_location_name;
      }
      return output;
    case CustomerActivityType.addedAsEventParticipant:
      output = 'Was added as an event participant';
      if (includeLocation && customer.last_activity_location_name) {
        output += ' at ' + customer.last_activity_location_name;
      }
      return output;
    case CustomerActivityType.profileEdited:
      return 'Profile edited';
    default:
      return 'Unknown activity type';
  }
}

export interface CustomerClimberAppTokenResponse {
  user_id: number;
  user_token: string;
  customer_token?: string;
}
